import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { useThemePresetStore } from "../store/theme-preset-store";
import { ThemePreset } from "../types/theme";
import { getPresetThemeStyles } from "../utils/theme-preset-helper";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Heart,
  Search,
  Settings,
  Shuffle,
  Sun,
} from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

interface ThemePresetSelectProps extends React.ComponentProps<typeof Button> {
  withCycleThemes?: boolean;
  value?: string;
  onValueChange?: (presetName: string) => void;
}

interface ColorBoxProps {
  color: string;
}

const ColorBox: React.FC<ColorBoxProps> = ({ color }) => (
  <div className="border-muted h-3 w-3 rounded-sm border" style={{ backgroundColor: color }} />
);

interface ThemeColorsProps {
  presetName: string;
  mode: "light" | "dark";
  styles?: Record<string, string>;
}

const ThemeColors: React.FC<ThemeColorsProps> = ({ presetName, mode, styles: providedStyles }) => {
  const styles = providedStyles ?? getPresetThemeStyles(presetName)[mode];
  return (
    <div className="flex gap-0.5">
      <ColorBox color={styles.background} />
      <ColorBox color={styles.card} />
      <ColorBox color={styles.primary} />
      <ColorBox color={styles.border} />
    </div>
  );
};

const isThemeNew = (preset: ThemePreset) => {
  if (!preset.createdAt) return false;
  const createdAt = new Date(preset.createdAt);
  const timePeriod = new Date();
  timePeriod.setDate(timePeriod.getDate() - 5);
  return createdAt > timePeriod;
};

const ThemeControls = ({ onSelectPreset }: { onSelectPreset?: (presetName: string) => void }) => {
  const editorStore = useEditorStore();
  const applyThemePreset = editorStore.applyThemePreset;
  const themePresetStore = useThemePresetStore();
  const presets = themePresetStore.getAllPresets();

  const presetNames = useMemo(() => ["default", ...Object.keys(presets)], [presets]);

  const randomize = useCallback(() => {
    const random = Math.floor(Math.random() * presetNames.length);
    if (onSelectPreset) {
      onSelectPreset(presetNames[random]);
      return;
    }
    applyThemePreset(presetNames[random]);
  }, [presetNames, applyThemePreset, onSelectPreset]);

  return (
    <div className="flex gap-1">
      <Button variant="ghost" size="icon" className="size-6 p-1">
        <Sun className="h-3.5 w-3.5" />
      </Button>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="size-6 p-1" onClick={randomize}>
            <Shuffle className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Random theme</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

interface ThemeCycleButtonProps extends React.ComponentProps<typeof Button> {
  direction: "prev" | "next";
}

const ThemeCycleButton: React.FC<ThemeCycleButtonProps> = ({
  direction,
  onClick,
  className,
  ...props
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className={cn("aspect-square h-full shrink-0", className)}
        onClick={onClick}
        {...props}
      >
        {direction === "prev" ? (
          <ArrowLeft className="h-4 w-4" />
        ) : (
          <ArrowRight className="h-4 w-4" />
        )}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{direction === "prev" ? "Previous theme" : "Next theme"}</TooltipContent>
  </Tooltip>
);

interface ThemePresetCycleControlsProps extends React.ComponentProps<typeof Button> {
  filteredPresets: string[];
  currentPresetName: string;
  className?: string;
  onSelectPreset?: (presetName: string) => void;
}

const ThemePresetCycleControls: React.FC<ThemePresetCycleControlsProps> = ({
  filteredPresets,
  currentPresetName,
  className,
  onSelectPreset,
  ...props
}) => {
  const editorStore = useEditorStore();
  const applyThemePreset = editorStore.applyThemePreset;

  const currentIndex =
    useMemo(
      () => filteredPresets.indexOf(currentPresetName || "default"),
      [filteredPresets, currentPresetName]
    ) ?? 0;

  const cycleTheme = useCallback(
    (direction: "prev" | "next") => {
      const newIndex =
        direction === "next"
          ? (currentIndex + 1) % filteredPresets.length
          : (currentIndex - 1 + filteredPresets.length) % filteredPresets.length;
      if (onSelectPreset) {
        onSelectPreset(filteredPresets[newIndex]);
        return;
      }
      applyThemePreset(filteredPresets[newIndex]);
    },
    [currentIndex, filteredPresets, applyThemePreset, onSelectPreset]
  );
  return (
    <>
      <Separator orientation="vertical" className="min-h-8" />

      <ThemeCycleButton
        direction="prev"
        size="icon"
        className={cn("aspect-square min-h-8 w-auto", className)}
        onClick={() => cycleTheme("prev")}
        {...props}
      />

      <Separator orientation="vertical" className="min-h-8" />

      <ThemeCycleButton
        direction="next"
        size="icon"
        className={cn("aspect-square min-h-8 w-auto", className)}
        onClick={() => cycleTheme("next")}
        {...props}
      />
    </>
  );
};

const ThemePresetSelect: React.FC<ThemePresetSelectProps> = ({
  withCycleThemes = true,
  className,
  value,
  onValueChange,
  ...props
}) => {
  const editorStore = useEditorStore();
  const themeState = editorStore.themeState;
  const currentPreset = value ?? themeState.preset;
  const mode = themeState.currentMode;
  const applyThemePreset = editorStore.applyThemePreset;
  const hasUnsavedChanges = editorStore.hasUnsavedChanges;
  const isDraftMode = typeof onValueChange === "function";

  const themePresetStore = useThemePresetStore();
  const presets = themePresetStore.getAllPresets();

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const isSavedTheme = useCallback(
    (presetId: string) => {
      return presets[presetId]?.source === "SAVED";
    },
    [presets]
  );

  const presetNames = useMemo(() => ["default", ...Object.keys(presets)], [presets]);

  const filteredPresets = useMemo(() => {
    const filteredList =
      search.trim() === ""
        ? presetNames
        : presetNames.filter((name) => {
            if (name === "default") {
              return "default".toLowerCase().includes(search.toLowerCase());
            }
            return presets[name]?.label?.toLowerCase().includes(search.toLowerCase());
          });

    // Separate saved and default themes
    const savedThemesList = filteredList.filter((name) => name !== "default" && isSavedTheme(name));
    const defaultThemesList = filteredList.filter((name) => !savedThemesList.includes(name));

    // Sort each list, with "default" at the top for default themes
    const sortThemes = (list: string[]) => {
      const defaultTheme = list.filter((name) => name === "default");
      const otherThemes = list
        .filter((name) => name !== "default")
        .sort((a, b) => {
          const labelA = presets[a]?.label || a;
          const labelB = presets[b]?.label || b;
          return labelA.localeCompare(labelB);
        });
      return [...defaultTheme, ...otherThemes];
    };

    // Combine saved themes first, then default themes
    return [...sortThemes(savedThemesList), ...sortThemes(defaultThemesList)];
  }, [presetNames, search, presets, isSavedTheme]);

  const filteredSavedThemes = useMemo(() => {
    return filteredPresets.filter((name) => name !== "default" && isSavedTheme(name));
  }, [filteredPresets, isSavedTheme]);

  const filteredDefaultThemes = useMemo(() => {
    return filteredPresets.filter((name) => name === "default" || !isSavedTheme(name));
  }, [filteredPresets, isSavedTheme]);

  const currentPresetName = presetNames?.find((name) => name === currentPreset);
  const currentPresetStyles =
    (currentPreset && currentPreset !== "default" ? presets[currentPreset]?.styles?.[mode] : undefined) ??
    getPresetThemeStyles(currentPresetName || "default")[mode];

  const selectPreset = useCallback(
    (presetName: string) => {
      if (onValueChange) {
        onValueChange(presetName);
      } else {
        applyThemePreset(presetName);
      }
      setSearch("");
      setOpen(false);
    },
    [applyThemePreset, onValueChange]
  );

  return (
    <TooltipProvider>
      <div className="flex w-full items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn("group relative w-full justify-between md:min-w-56", className)}
            {...props}
          >
            <div className="flex w-full items-center gap-3 overflow-hidden">
              <div className="flex gap-0.5">
                <ColorBox color={currentPresetStyles?.background || '#ffffff'} />
                <ColorBox color={currentPresetStyles?.card || '#ffffff'} />
                <ColorBox color={currentPresetStyles?.primary || '#3b82f6'} />
                <ColorBox color={currentPresetStyles?.border || '#e5e7eb'} />
              </div>
              {currentPresetName !== "default" &&
                currentPresetName &&
                isSavedTheme(currentPresetName) &&
                !isDraftMode &&
                !hasUnsavedChanges() && (
                  <div className="bg-muted rounded-full p-1">
                    <Heart
                      className="size-1"
                      stroke="var(--muted)"
                      fill="var(--muted-foreground)"
                    />
                  </div>
                )}
              <span className="truncate text-left font-medium capitalize">
                {!isDraftMode && hasUnsavedChanges() ? (
                  <>Custom (Unsaved)</>
                ) : (
                  currentPresetName === "default"
                    ? "Default"
                    : currentPresetName ? presets[currentPresetName]?.label || currentPresetName : "Default"
                )}
              </span>
            </div>
            <ChevronDown className="size-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] max-h-[min(var(--radix-popover-content-available-height),24rem)] overflow-hidden p-0"
          align="center"
          collisionPadding={16}
        >
          <Command className="w-full">
            <div className="flex w-full items-center">
              <div className="flex w-full items-center border-b px-3 py-1">
                <Search className="size-4 shrink-0 opacity-50" />
                <Input
                  placeholder="Search themes..."
                  className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <div className="text-muted-foreground text-sm">
                {filteredPresets.length} theme
                {filteredPresets.length !== 1 ? "s" : ""}
              </div>
              <ThemeControls onSelectPreset={onValueChange ? selectPreset : undefined} />
            </div>
            <Separator />
            <ScrollArea className="h-[min(20rem,calc(var(--radix-popover-content-available-height)_-_5.75rem))]">
              <CommandEmpty>No themes found.</CommandEmpty>

              {/* Saved Themes Group */}
              {filteredSavedThemes.length > 0 && (
                <>
                  <CommandGroup
                    heading={
                      <div className="flex w-full items-center justify-between">
                        <span>Saved Themes</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 p-0 text-xs"
                          disabled
                        >
                          <span>Manage</span>
                          <Settings className="size-3.5!" />
                        </Button>
                      </div>
                    }
                  >
                    {filteredSavedThemes
                      .filter((name) => name !== "default" && isSavedTheme(name))
                      .map((presetName, index) => (
                        <CommandItem
                          key={`${presetName}-${index}`}
                          className="data-[highlighted]:bg-secondary/50 flex items-center gap-2 py-2"
                          onSelect={() => selectPreset(presetName)}
                        >
                          <ThemeColors presetName={presetName} mode={mode} styles={presets[presetName]?.styles?.[mode]} />
                          <div className="flex flex-1 items-center gap-2">
                            <span className="line-clamp-1 text-sm font-medium capitalize">
                              {presetName === "default"
                                ? "Default"
                                : presets[presetName]?.label || presetName}
                            </span>
                            {presetName !== "default" && presets[presetName] && isThemeNew(presets[presetName]) && (
                              <Badge variant="secondary" className="rounded-full text-xs">
                                New
                              </Badge>
                            )}
                          </div>
                          {presetName === currentPresetName && (
                            <Check className="h-4 w-4 shrink-0 opacity-70" />
                          )}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                  <Separator className="my-2" />
                </>
              )}

              {filteredSavedThemes.length === 0 && search.trim() === "" && (
                <>
                  <div className="text-muted-foreground flex items-center gap-1.5 px-3 py-2 text-xs font-medium">
                    <div className="bg-muted flex items-center gap-1 rounded-md border px-2 py-0.5">
                      <Heart className="fill-muted-foreground size-3" />
                      <span>Save</span>
                    </div>
                    <span className="text-muted-foreground">a theme to find it here.</span>
                  </div>
                  <Separator />
                </>
              )}

              {/* Default Theme Group */}
              {filteredDefaultThemes.length > 0 && (
                <CommandGroup heading="Built-in Themes">
                  {filteredDefaultThemes.map((presetName, index) => (
                    <CommandItem
                      key={`${presetName}-${index}`}
                      className="data-[highlighted]:bg-secondary/50 flex items-center gap-2 py-2"
                      onSelect={() => selectPreset(presetName)}
                    >
                      <ThemeColors presetName={presetName} mode={mode} styles={presets[presetName]?.styles?.[mode]} />
                      <div className="flex flex-1 items-center gap-2">
                        <span className="text-sm font-medium capitalize">
                          {presetName === "default"
                            ? "Default"
                            : presets[presetName]?.label || presetName}
                        </span>
                        {presetName !== "default" && presets[presetName] && isThemeNew(presets[presetName]) && (
                          <Badge variant="secondary" className="rounded-full text-xs">
                            New
                          </Badge>
                        )}
                      </div>
                      {presetName === currentPresetName && (
                        <Check className="h-4 w-4 shrink-0 opacity-70" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </ScrollArea>
          </Command>
        </PopoverContent>
      </Popover>

      {withCycleThemes && (
        <ThemePresetCycleControls
          filteredPresets={filteredPresets}
          currentPresetName={currentPresetName || "default"}
          className={className}
          disabled={props.disabled}
          onSelectPreset={onValueChange ? selectPreset : undefined}
        />
      )}
      </div>
    </TooltipProvider>
  );
};

export default ThemePresetSelect;
